import { TournamentTeamStatus } from '@okletsplay/core';
import { KnockoutTournamentEntity, TournamentEntity } from 'app/interfaces/entity';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ModalName } from 'app/enums';
import { EntityStoreName } from 'app/enums/store';
import { useEntityRepositoryStatus } from 'app/hooks/store';
import { modalsReducers } from 'app/store/modal/slice';
import { dispatchUnwrap } from 'app/utils/store';
import { BracketTournamentCodeModalPayload } from '../../bracket/components/BracketTournamentCodeModal';
import { useRoundBreakDelay } from '../../bracket/hooks/use-round-break-delay';
import { currentTournamentMatchGameSelector } from '../store/current-tournament-match-game/selectors';
import { CurrentTournamentStoreStatus } from '../store/current-tournament/enum';
import { markAsReady } from '../store/current-tournament/repository';
import { currentTournamentSelector } from '../store/current-tournament/selectors';
import { useTournamentMatchTitle } from './use-tournament-match-title';

export const useTournamentAlertSettings = () => {
  const dispatch = useDispatch();
  const tournament: TournamentEntity = useSelector(currentTournamentSelector.tournament);
  const isUnlimitedJoinTimer = useSelector(currentTournamentSelector.isUnlimitedJoinTimer);
  const activeTournamentMatchGame = useSelector(currentTournamentMatchGameSelector.matchGame);
  const joinMatchGameTimer: number = useSelector(currentTournamentSelector.joinMatchGameTimer);
  const isUserJoinedToTheGame = useSelector(currentTournamentSelector.isUserJoinedToTheGame);
  const nextUserMatchGame = useSelector(currentTournamentSelector.nextUserMatchGame);
  const myTeam = useSelector(currentTournamentSelector.myTeam);

  const { gameBreakDuration } = useRoundBreakDelay(
    nextUserMatchGame?.roundId,
    nextUserMatchGame?.groupId
  );

  const isLoading = useEntityRepositoryStatus({
    [EntityStoreName.CurrentTournament]: CurrentTournamentStoreStatus.Joining,
  });

  const { getTournamentMatchGameTitle } = useTournamentMatchTitle();
  const matchGameTitle = getTournamentMatchGameTitle(
    activeTournamentMatchGame,
    tournament as KnockoutTournamentEntity
  );

  const isMyTeamDisqualified = useMemo(
    () => myTeam?.status === TournamentTeamStatus.Disqualified,
    [myTeam?.status]
  );

  const tournamentTitle = useMemo(() => tournament?.name, [tournament?.name]);

  const modalPayload: BracketTournamentCodeModalPayload = useMemo(
    () => ({
      tournamentId: tournament.id,
      tournamentMatchGameId: activeTournamentMatchGame?.matchGameId,
    }),
    [activeTournamentMatchGame?.matchGameId, tournament]
  );

  const isBreakTimerAvailable = useMemo(() => {
    return activeTournamentMatchGame.startedAt && !isUserJoinedToTheGame && !isUnlimitedJoinTimer;
  }, [activeTournamentMatchGame.startedAt, isUserJoinedToTheGame, isUnlimitedJoinTimer]);

  const onClickHandler = useCallback(() => {
    if (isUserJoinedToTheGame || isUnlimitedJoinTimer) {
      dispatch(modalsReducers.openModal({ name: ModalName.TournamentCode, data: modalPayload }));
      return;
    }

    dispatchUnwrap(
      markAsReady({
        tournamentId: tournament.id,
        payload: { gameId: activeTournamentMatchGame.gameId as string },
      }),
      {
        success: () =>
          dispatch(
            modalsReducers.openModal({ name: ModalName.TournamentCode, data: modalPayload })
          ),
      }
    );
  }, [
    activeTournamentMatchGame.gameId,
    dispatch,
    isUnlimitedJoinTimer,
    isUserJoinedToTheGame,
    modalPayload,
    tournament.id,
  ]);

  return {
    tournamentTitle,
    activeTournamentMatchGame,
    isMyTeamDisqualified,
    matchGameTitle,
    isLoading,
    onClickHandler,
    nextUserMatchGame,
    gameBreakDuration,
    isBreakTimerAvailable,
    joinMatchGameTimer,
  };
};
